import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { C, card, label, btn, input } from "../lib/theme";
import PlaceField from "../lib/PlaceField";
import { api } from "../lib/api";

const QUAL = ["good", "challenging", "bad", "neutral", "milestone"];
const CHANGE = ["beginning", "ending", "continuation", "unsure"];
const blank = (q) => ({ precision: q.precision_hint || "month", date: "", ym: "",
  year: "", quality: "milestone", change: "beginning", label: "" });

export default function Intake() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [entries, setEntries] = useState({});          // qIndex -> [entry]
  const [birth, setBirth] = useState({ date: "", time: "12:00", lat: "", lon: "", place: "" });
  const [state, setState] = useState({ err: "", done: null, busy: false });

  useEffect(() => {
    api.intakeGet(token).then((d) => {
      setForm(d);
      setEntries(Object.fromEntries(d.questions.map((q, i) => [i, [blank(q)]])));
    }).catch((e) => setState((s) => ({ ...s, err: e.message })));
  }, [token]);

  if (state.done) return (
    <Shell>
      <div style={{ ...card, textAlign: "center", padding: "44px 26px" }}>
        <div style={{ fontSize: 36, color: C.brass, marginBottom: 10 }}>✓</div>
        <div style={{ fontFamily: C.displayFont, fontSize: 25, marginBottom: 8 }}>
          Your answers have been submitted</div>
        <div style={{ fontSize: 13.5, color: C.dim, lineHeight: 1.7 }}>
          {typeof state.done === "string" ? state.done
            : "Thank you — your astrologer has received your history."}
          <br />You can close this tab.
        </div>
      </div>
    </Shell>);
  if (state.err && !form) return (
    <Shell><div style={{ ...card, color: C.rose }}>{state.err}</div></Shell>);
  if (!form) return <Shell><div style={{ color: C.faint }}>Loading…</div></Shell>;

  const setEntry = (qi, ei, patch) => setEntries({ ...entries,
    [qi]: entries[qi].map((e, k) => (k === ei ? { ...e, ...patch } : e)) });
  const dateOf = (e) => e.precision === "day" ? e.date
    : e.precision === "month" ? (e.ym && `${e.ym}-15`) : (e.year && `${e.year}-07-01`);
  const filled = [];
  form.questions.forEach((q, qi) => (entries[qi] || []).forEach((e) => {
    const d = dateOf(e);
    if (d) filled.push({ life_area: q.life_area, event_date: d, precision: e.precision,
      quality: e.quality, change: e.change,
      label: e.label || q.prompt.slice(0, 80) });
  }));

  const submit = async () => {
    setState((s) => ({ ...s, busy: true, err: "" }));
    try {
      const body = { answers: filled };
      if (form.needs_birth_data) {
        body.birth = { birth_local: `${birth.date}T${birth.time}`,
          lat: Number(birth.lat), lon: Number(birth.lon), place_label: birth.place };
        body.client_label = "Intake client";
      }
      const d = await api.intakeSubmit(token, body);
      setState({ err: "", busy: false, done: d?.message || true });
    } catch (e) { setState((s) => ({ ...s, busy: false, err: e.message })); }
  };

  return (
    <Shell>
      <div style={{ fontFamily: C.displayFont, fontSize: 27 }}>{form.title}</div>
      <div style={{ fontSize: 11.5, color: C.faint, marginBottom: 12 }}>
        sent to you by your astrologer</div>
      {form.instructions && (
        <div style={{ ...card, marginBottom: 10, fontSize: 12.5, color: C.dim,
          lineHeight: 1.65 }}>{form.instructions}</div>)}
      <div style={{ ...card, marginBottom: 14, fontSize: 11.5, color: C.faint,
        lineHeight: 1.6 }}>{form.privacy_note}</div>

      {form.needs_birth_data && (
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={label}>YOUR BIRTH DETAILS</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <input type="date" value={birth.date}
              onChange={(e) => setBirth({ ...birth, date: e.target.value })} style={input} />
            <input type="time" value={birth.time}
              onChange={(e) => setBirth({ ...birth, time: e.target.value })} style={input} />
          </div>
          <PlaceField initialPlace={birth.place}
            search={(q) => api.intakeGeocode(token, q)}
            onResolve={({ place, lat, lon }) => setBirth((b) => ({ ...b, place, lat, lon }))} />
        </div>)}

      {form.questions.map((q, qi) => (
        <div key={qi} style={{ ...card, marginBottom: 10 }}>
          <div style={{ fontSize: 13.5, marginBottom: 8 }}>{q.prompt}</div>
          {(entries[qi] || []).map((e, ei) => (
            <div key={ei} style={{ display: "flex", gap: 6, flexWrap: "wrap",
              alignItems: "center", padding: "6px 0",
              borderTop: ei ? `1px solid ${C.line}` : "none" }}>
              <select value={e.precision} onChange={(ev) =>
                setEntry(qi, ei, { precision: ev.target.value })} style={input}>
                <option value="day">exact day</option>
                <option value="month">month</option>
                <option value="year">year</option>
              </select>
              {e.precision === "day" &&
                <input type="date" value={e.date}
                  onChange={(ev) => setEntry(qi, ei, { date: ev.target.value })} style={input} />}
              {e.precision === "month" &&
                <input type="month" value={e.ym}
                  onChange={(ev) => setEntry(qi, ei, { ym: ev.target.value })} style={input} />}
              {e.precision === "year" &&
                <input type="number" placeholder="e.g. 1999" min="1920" max="2026"
                  value={e.year} onChange={(ev) => setEntry(qi, ei, { year: ev.target.value })}
                  style={{ ...input, width: 100 }} />}
              <select value={e.quality} onChange={(ev) =>
                setEntry(qi, ei, { quality: ev.target.value })} style={input}>
                {QUAL.map((v) => <option key={v}>{v}</option>)}
              </select>
              <select value={e.change} onChange={(ev) =>
                setEntry(qi, ei, { change: ev.target.value })} style={input}>
                {CHANGE.map((v) => <option key={v}>{v}</option>)}
              </select>
              <input placeholder="Brief note" value={e.label}
                onChange={(ev) => setEntry(qi, ei, { label: ev.target.value })}
                style={{ ...input, flexGrow: 1, minWidth: 140 }} />
              {(entries[qi].length > 1) && (
                <button onClick={() => setEntries({ ...entries,
                  [qi]: entries[qi].filter((_x, k) => k !== ei) })}
                  style={{ all: "unset", cursor: "pointer", color: C.faint }}>✕</button>)}
            </div>))}
          {q.multi && (
            <button onClick={() => setEntries({ ...entries,
              [qi]: [...entries[qi], blank(q)] })}
              style={{ all: "unset", cursor: "pointer", color: C.brass, fontSize: 12,
                marginTop: 4 }}>+ add another</button>)}
        </div>))}

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 6 }}>
        <button style={btn()} disabled={state.busy || filled.length === 0 ||
          (form.needs_birth_data && (!birth.date || birth.lat === "" || birth.lon === ""))}
          onClick={submit}>
          {state.busy ? "Sending…" : `Send ${filled.length} dated event${filled.length === 1 ? "" : "s"} to your astrologer`}
        </button>
        <span style={{ fontSize: 11, color: C.faint }}>Skipped questions are fine.</span>
      </div>
      {state.err && <div style={{ color: C.rose, fontSize: 12, marginTop: 8 }}>{state.err}</div>}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text,
      fontFamily: C.font, padding: "30px 16px 60px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>{children}</div>
    </div>
  );
}
